/* @section: excel-export */
import * as XLSX from "xlsx";
import type { GeneratedTable } from "./types";
import { colLetter } from "./generator";

/**
 * Construit et déclenche le téléchargement d'un fichier .xlsx à partir
 * d'un tableau généré. Les colonnes de type "formula" reçoivent une vraie
 * formule Excel (objet { f: "..." }) afin d'être recalculées dans Excel.
 */
export function exportToExcel(table: GeneratedTable): void {
  const { columns, rows, title } = table;

  // Matrice AOA : ligne 0 = en-têtes
  const aoa: unknown[][] = [];
  aoa.push(columns.map((c) => c.name));

  rows.forEach((row, rIdx) => {
    const excelRow: unknown[] = [];
    // numéro de ligne Excel réel : +2 (1 pour 1-based, 1 pour l'en-tête)
    const excelRowNumber = rIdx + 2;
    columns.forEach((c, cIdx) => {
      if (c.type === "formula" && c.formula) {
        const f = c.formula.replace(/\{row\}/g, String(excelRowNumber));
        // f sans le "=" pour SheetJS
        excelRow[cIdx] = { t: "n", f: f.replace(/^=/, "") };
      } else {
        const val = row[cIdx];
        if (c.type === "number" || c.type === "currency" || c.type === "percent") {
          excelRow[cIdx] = typeof val === "number" ? val : val === "" ? "" : Number(val);
        } else {
          excelRow[cIdx] = val ?? "";
        }
      }
    });
    aoa.push(excelRow);
  });

  const ws = XLSX.utils.aoa_to_sheet(aoa);

  // Largeurs de colonnes
  ws["!cols"] = columns.map((c) => ({ wch: c.width ?? 14 }));

  // Formats numériques par colonne
  const lastRow = rows.length + 1; // 1-based, incluant en-tête
  columns.forEach((c, cIdx) => {
    const letter = colLetter(cIdx);
    let fmt: string | null = null;
    if (c.type === "currency") fmt = "#,##0.00\\ \u20ac";
    else if (c.type === "percent") fmt = "0.0%";
    else if (c.type === "number") fmt = "#,##0";
    // colonnes formule : deviner selon le nom
    if (c.type === "formula") {
      if (/ttc|ht|total|co[uû]t|valeur|salaire|cotisation|panier|montant|tva|solde/i.test(c.name)) fmt = "#,##0.00\\ \u20ac";
      else if (/jours?/i.test(c.name)) fmt = "0";
    }
    if (fmt) {
      for (let r = 2; r <= lastRow; r++) {
        const addr = `${letter}${r}`;
        if (ws[addr]) ws[addr].z = fmt;
      }
    }
  });

  // Style minimal des en-têtes (gras) — supporté par xlsx en lecture/écriture basique
  columns.forEach((_, cIdx) => {
    const addr = `${colLetter(cIdx)}1`;
    if (ws[addr]) {
      ws[addr].s = { font: { bold: true } };
    }
  });

  const wb = XLSX.utils.book_new();
  const sheetName = title.slice(0, 31).replace(/[\\/?*[\]:]/g, "");
  XLSX.utils.book_append_sheet(wb, ws, sheetName || "Feuille1");

  const fileName = `${title.replace(/[^a-zA-Z0-9À-ÿ _-]/g, "").trim() || "tableau"}.xlsx`;
  XLSX.writeFile(wb, fileName, { compression: true });
}
